import { ref } from 'vue'
import { useReducedMotion } from '@vueuse/motion'

import type { Ref } from 'vue'
import type { Variant } from '@vueuse/motion'
import type { AnimationParticleItem, AnimationParticlesProps } from './interfaces'

import { generateParticles } from './particles'
import { ANIMATION_PARTICLES_DEFAULTS } from './animation-particles.config'

interface ParticleMotion {
  initial: Variant
  enter: Variant
}

interface UseAnimationParticlesOptions {
  props?: Required<AnimationParticlesProps>
}

interface UseAnimationParticlesReturn {
  particles: Ref<AnimationParticleItem[]>
  play: () => void
  getParticleMotion: (particle: AnimationParticleItem) => ParticleMotion
}

const particles = ref<AnimationParticleItem[]>([])
const config = ref<Required<AnimationParticlesProps>>(ANIMATION_PARTICLES_DEFAULTS)

const getParticlesCount = (): number => {
  if (window.innerWidth >= 1280) return config.value.desktopCount
  if (window.innerWidth >= 740) return config.value.tabletCount
  return config.value.count
}

export const useAnimationParticles = ({ props }: UseAnimationParticlesOptions = {}): UseAnimationParticlesReturn => {
  if (props) config.value = props

  const reducedMotion = useReducedMotion()

  const play = (): void => {
    if (reducedMotion.value) return

    const batch = generateParticles({
      count: getParticlesCount(),
      minSize: config.value.minSize,
      maxSize: config.value.maxSize,
    })
    particles.value = [...particles.value, ...batch]

    const lifetime = Math.max(...batch.map(particle => particle.delay + particle.duration)) + config.value.cleanupGap
    const batchIds = new Set(batch.map(particle => particle.id))

    setTimeout(() => {
      particles.value = particles.value.filter(particle => !batchIds.has(particle.id))
    }, lifetime)
  }

  const getParticleMotion = (particle: AnimationParticleItem): ParticleMotion => ({
    initial: {
      y: -particle.size * 2,
      x: 0,
      rotate: 0,
      opacity: 0,
    },
    enter: {
      y: window.innerHeight + particle.size * 2,
      x: particle.drift,
      rotate: particle.rotate,
      opacity: 1,
      transition: {
        y: { delay: particle.delay, duration: particle.duration, ease: 'easeIn' },
        x: { delay: particle.delay, duration: particle.duration, ease: 'linear' },
        rotate: { delay: particle.delay, duration: particle.duration, ease: 'linear' },
        opacity: { delay: particle.delay, duration: 200, ease: 'easeOut' },
      },
    },
  })

  return {
    particles,
    play,
    getParticleMotion,
  }
}
